import React from 'react';
import { useTranslation } from 'react-i18next';
import { TimeSeriesBarChart, TimeSeriesLineChart, type ChartSeries, type TimePoint } from '@org/ui';
import { useAnalytics } from './analytics-context';
import { ReportCard } from './report-card';
import { SeriesTable } from './series-table';
import { formatBucket } from './format';
import { mergeComparison } from './compare';

type TimeSeriesCardProps = {
  title: string;
  description?: string;
  data: TimePoint[] | undefined;
  /** The same series over the comparison window (from `compareInput`). */
  cmpData?: TimePoint[] | undefined;
  series: ChartSeries[];
  isLoading?: boolean;
  error?: unknown;
  /** Bars for counts (default), lines for rates. Comparing always draws lines. */
  kind?: 'bar' | 'line';
  stacked?: boolean;
  valueFormat?: (n: number) => string;
  yDomain?: [number, number];
  height?: number;
  actions?: React.ReactNode;
  /** Extra content above the chart (tiles etc.). */
  children?: React.ReactNode;
};

function hasAnyValue(data: TimePoint[] | undefined, series: ChartSeries[]): boolean {
  if (!data) return false;
  return data.some((p) => series.some((s) => typeof p[s.key] === 'number' && (p[s.key] as number) !== 0));
}

/**
 * The standard time-series report: a chart, a table twin, and the
 * comparison window laid over it as dashed lines of the same colour.
 */
export function TimeSeriesCard({
  title,
  description,
  data,
  cmpData,
  series,
  isLoading,
  error,
  kind = 'bar',
  stacked,
  valueFormat,
  yDomain,
  height,
  actions,
  children,
}: TimeSeriesCardProps) {
  const { t, i18n } = useTranslation('admin');
  const { grain, search, compareInput } = useAnalytics();
  const comparing = Boolean(compareInput && cmpData);
  const cmpLabel = (label: string) =>
    `${label} (${t(search.compare === 'lastYear' ? 'analytics.compare.lastYearShort' : 'analytics.compare.previousShort')})`;

  const merged = React.useMemo(
    () => mergeComparison(data ?? [], comparing ? cmpData : null, series, cmpLabel),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, cmpData, series, comparing, search.compare, i18n.language],
  );
  const xLabel = (b: string) => formatBucket(b, grain, i18n.language);

  return (
    <ReportCard
      title={title}
      description={description}
      isLoading={isLoading}
      error={error}
      isEmpty={!hasAnyValue(data, series) && !hasAnyValue(cmpData, series)}
      actions={actions}
      table={<SeriesTable data={merged.data} series={merged.series} grain={grain} valueFormat={valueFormat} />}
    >
      {children}
      {comparing || kind === 'line' ? (
        <TimeSeriesLineChart
          data={merged.data}
          series={merged.series}
          xLabelFormat={xLabel}
          valueFormat={valueFormat}
          yDomain={yDomain}
          height={height}
          emptyLabel={t('analytics.common.noData')}
        />
      ) : (
        <TimeSeriesBarChart
          data={merged.data}
          series={merged.series}
          stacked={stacked}
          xLabelFormat={xLabel}
          valueFormat={valueFormat}
          height={height}
          emptyLabel={t('analytics.common.noData')}
        />
      )}
    </ReportCard>
  );
}
